import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { getClientContext } from '@shared/utils/get-client-context';

// all known variants of "the lazy-loaded chunk this tab is asking
// for no longer exists on the server"
// (typically because a new deploy replaced it with a new hash).
const CHUNK_LOAD_ERROR_PATTERN =
  /failed to fetch dynamically imported module|failed to load module script|importing a module script failed|error loading dynamically imported module/i;
const CHUNK_RELOAD_STORAGE_KEY = 'chunkLoadErrorReloadedAt';
const CHUNK_RELOAD_COOLDOWN_MS = 60_000;

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  handleError(error: unknown): void {
    console.error(error);

    if (error instanceof HttpErrorResponse) {
      return;
    }

    if (this.isChunkLoadError(error)) {
      this.recoverFromChunkLoadError();
      return;
    }

    Sentry.withScope((scope) => {
      scope.setContext('client', getClientContext());
      Sentry.captureException(error);
    });
  }

  private isChunkLoadError(error: unknown): boolean {
    const message =
      error instanceof Error ? error.message : String(error ?? '');
    return CHUNK_LOAD_ERROR_PATTERN.test(message);
  }

  // The tab is running an older build than what's currently deployed -
  // a hard reload fetches the fresh index.html and chunk manifest
  private recoverFromChunkLoadError(): void {
    const lastReload = Number(
      sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY) || 0,
    );
    const alreadyReloadedRecently =
      Date.now() - lastReload < CHUNK_RELOAD_COOLDOWN_MS;

    Sentry.withScope((scope) => {
      scope.setContext('client', getClientContext());
      Sentry.captureMessage(
        alreadyReloadedRecently
          ? 'Chunk load error persisted after reload'
          : 'Chunk load error - reloading to fetch latest build',
        alreadyReloadedRecently ? 'error' : 'info',
      );
    });

    if (alreadyReloadedRecently) return;

    sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(Date.now()));
    window.location.reload();
  }
}
